import React from 'react';
import { Alert, Card, CardHeader, EmptyState, Skeleton } from '@opendate-app/setlist';
import { changePercent } from '../../utils/reportingUtils';

const count = new Intl.NumberFormat('en-US');
const LABEL_STYLE = { fontSize: 12, color: 'var(--sl-text-tertiary)' };

function BuyerChange({ current, prior }) {
  const change = changePercent(current, prior);
  if (change === null) return null;

  const up = parseFloat(change) >= 0;
  return (
    <span style={{ fontSize: 13, fontWeight: 600, color: up ? 'var(--sl-text-success)' : 'var(--sl-text-error)' }}>
      {`${up ? '+' : '−'}${change.replace('-', '')}%`}
    </span>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={LABEL_STYLE}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{value}</div>
      {hint && <div style={LABEL_STYLE}>{hint}</div>}
    </div>
  );
}

function AudienceCard({ data, loading, error, href, showPurchaseCaption }) {
  const buyers = data?.unique_buyers || 0;
  const firstPct = buyers > 0 ? Math.round(((data.first_time || 0) / buyers) * 100) : 0;
  const ageUnknown = data?.median_age == null;

  return (
    <Card
      header={(
        <CardHeader
          title={<a href={href} style={{ color: 'inherit' }}>Audience →</a>}
          supportingText={showPurchaseCaption ? 'By purchase date' : undefined}
        />
      )}
    >
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} width="100%" height={24} />)}
        </div>
      )}
      {!loading && error && <Alert color="error" title="Couldn't load audience" description="Refresh the page to try again." />}
      {!loading && !error && buyers === 0 && <EmptyState size="sm" title="No buyers in this range" />}
      {!loading && !error && buyers > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 24, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{count.format(buyers)}</span>
              <span style={{ color: 'var(--sl-text-secondary)' }}>unique buyers</span>
              <BuyerChange current={buyers} prior={data.prior_unique_buyers} />
            </div>
            <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', marginTop: 8, background: 'var(--sl-bg-brand-secondary)' }}>
              <div style={{ width: `${firstPct}%`, background: 'var(--sl-bg-brand-solid)' }} />
            </div>
            <div style={{ ...LABEL_STYLE, marginTop: 6 }}>{`${firstPct}% first-time · ${100 - firstPct}% returning`}</div>
          </div>
          {data.top_markets.length > 0 && (
            <div>
              <div style={LABEL_STYLE}>Top markets</div>
              {data.top_markets.map((market) => (
                <div key={`${market.city}-${market.state}`} style={{ display: 'flex', fontSize: 13, marginTop: 6 }}>
                  <span style={{ flex: 1, color: 'var(--sl-text-secondary)' }}>{market.state ? `${market.city}, ${market.state}` : market.city}</span>
                  <span style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{`${Math.round(market.pct)}%`}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16 }}>
            <Stat label="Median age" value={ageUnknown ? '—' : data.median_age} hint={ageUnknown ? 'Not enough age data' : null} />
            <Stat label="Opted in to SMS" value={`${Math.round(data.sms_opt_in_pct)}%`} />
          </div>
          <div style={LABEL_STYLE}>SMS opt-in is as of today.</div>
        </div>
      )}
    </Card>
  );
}

export default AudienceCard;
